import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { Button, Progress } from 'reactstrap';
import Dropzone from 'react-dropzone';
import { get, some } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Papaparse from 'papaparse';
import { connect } from 'react-redux';

import getSpreadsheetError from '../diy-utils/getSpreadsheetError';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import testPreUploadErrors from '../diy-utils/SpreadsheetUtils';
import { updateHouse } from '../../../modules/house';

/**
 * This class represents the spreadsheet uploader used in DIY Step3.  I originally
 * attempted to make this as reusable as possible but for the sake of readability and
 * due to all of the customizations in place - we should start the multi-image uploader
 * from scratch, using this as a guideline.
 */
class Step3Dropzone extends Component {
  constructor() {
    super();
    this.state = {
      files: [],
      postUploadErrors: null,
      postUploadErrorCount: 0,
      preUploadError: null,
      preUploadErrorTitle: '',
      preUploadErrorText: '',
      uploadFailed: false,
      isFailingInvNumber: false,
    };
  }

  componentDidMount() {
    this.props.dispatchUpdateHouse(this.props.house.id);
  }

  clearUploadErrors = () => {
    this.setState(
      {
        isUploading: false,
        postUploadErrors: null,
        postUploadErrorCount: 0,
        preUploadError: null,
        preUploadErrorTitle: '',
        preUploadErrorText: '',
        uploadFailed: false,
        isFailingInvNumber: false,
      },
      () => {
        // Cause the Save & Exit link to reappear
        this.props.setUploadErrors(false);
      }
    );
  };

  setBreakingError = postUploadErrors => {
    // Breaking errors - user must re-upload
    this.setState(
      {
        postUploadErrors,
        postUploadErrorCount: Object.keys(postUploadErrors).length,
        uploadFailed: true
      },
      () => {
        // Cause the Save & Exit link to disappear
        this.props.setUploadErrors(true);
      }
    );
  };

  // validate files using papaparse, this function will be return a promise
  validateCsv = (files) => {
    const lotNumber = 'Lot Number';
    const invNumber = 'Inv Number';
    let countErrors = 0;
    let isValid = true;
    return new Promise((resolve, reject) => {
      Papaparse.parse(files[0], {
        header: true,
        skipEmptyLines: true,
        complete: (lotsParsed) => {
          lotsParsed.data.forEach((lot) => {
            if (lot[lotNumber] === '' && lot[invNumber] === '') {
              countErrors++;
              isValid = false;
            }
          });
          resolve({ isValid, count: countErrors });
        },
        error: () => reject(),
      });
    });
  };

  // When a user drops or selects a file on the dropzone
  onDrop = async files => {
    this.clearUploadErrors();

    const { acceptedFileList, house } = this.props;
    const hasPreUploadErrors = testPreUploadErrors(files, acceptedFileList);
    if (hasPreUploadErrors) {
      const { preUploadError, preUploadErrorText, preUploadErrorTitle } = hasPreUploadErrors;
      this.setState({ preUploadError, preUploadErrorTitle, preUploadErrorText });
      return;
    }

    // validate user missing the correct connectType (platinum (e) or gold (p))
    const ycConnectTypes = ['e', 'p'];
    const isPlatinumGold = ycConnectTypes.indexOf(house.connectType) !== -1;

    try {
      // Validate csv file if
      if (isPlatinumGold) {
        const { isValid, count } = await this.validateCsv(files);
        // if valid, then continue with xhr request
        // but if not valid, then set the error in the state
        if (!isValid) {
          this.setState({
            isUploading: false,
            preUploadError: true,
            preUploadErrorTitle: 'Missing Lot or Inventory Number ',
            preUploadErrorText: `Lot or inventory number is required for every item. You have ${count} lot(s) without a number. Please correct all to proceed. If you are uploading items for the Private Label sale preview you only need inventory number. `,
            uploadFailed: true,
            isFailingInvNumber: true,
          });
        }
      }

      // Happy path, lets start uploading and building our request
      this.setState({ files, isUploading: true, progressPct: 0 });

      const { catalogID, packageID, selectedRadio } = this.props;
      const formData = new FormData();
      formData.append('csvType', `csv/${selectedRadio}`);
      formData.append('catalogID', catalogID);
      formData.append('packageID', packageID);
      formData.append('fileData', files[0]);

      /**
       * Using the XMLHttpRequest object, we gain more control over the request, which
       * allows us to do things such as control the timeout value, tap into the onprogress
       * event (create a progress bar)
       */
      const xhr = new XMLHttpRequest();
      // Timeout after two minutes
      xhr.timeout = 1000 * 60 * 2;
      xhr.open('POST', `/cps/app/spreadsheet/csv/${selectedRadio}?version=v2`);

      xhr.onprogress = progressEvent => {
        const { loaded } = progressEvent;
        /**
         * Sometimes the progress event returns a loaded size greater than the actual file
         * size.  This makes sure we return a maximum of 100% complete.
         */
        const capLoadedFileSize = Math.min(loaded, files[0].size);
        const progressPct = Math.round((capLoadedFileSize / files[0].size) * 100);
        this.setState({ progressPct, isUploading: true });
      };

      xhr.ontimeout = () => {
        this.setState({
          isUploading: false,
          preUploadError: true,
          preUploadErrorTitle: 'Your file upload has timed out.',
          preUploadErrorText: 'Please verify you are connected to the internet and that you are attaching a file in the correct format'
        });
      };

      xhr.onloadend = loadEvent => {
        const status = get(loadEvent, 'target.status', 400);
        const response = JSON.parse(get(loadEvent, 'target.response', {}));
        const warnings = get(response, 'warnings', null);

        if (status === 200) {
          // The user had no errors, hallelujah
          const lots = get(response, 'data.lots', []);
          /**
           * Add a unique ID attribute to the lots since the
           * spreadsheet error behavior depends on this
           */
          lots.forEach((lot, index) => {
            lot.id = index;
            // Also need this
            lot.hasError = false;
          });

          this.props.handleUpload(lots, [], warnings);
        }

        if (status === 504) {
          this.setState({
            isUploading: false,
            preUploadError: true,
            preUploadErrorTitle: 'Gateway timeout',
            preUploadErrorText:
              'There has been a loss of connection.  Please re-upload and try again.'
          });
          return;
        }

        if (status >= 400) {
          const {
            match: {
              params: { type }
            }
          } = this.props;

          if (this.state.isFailingInvNumber) {
            return;
          }

          // Spreadsheet Errors are non-breaking - the user will fix them through the UI
          const spreadsheetErrors = {};

          const errors = get(response, 'errors', []);
          errors.forEach(error => {
            const { displayMsg, errorCode, lotNumber, referenceMsg, invNumber } = error;
            let errorCodeKey = errorCode;
            /**
             * Error code 142 is actually several different errors
             * that have to be grouped differently...
             */
            if (errorCode === 142) {
              errorCodeKey = `${errorCode}-${displayMsg}`;
            }
            if (!spreadsheetErrors[errorCodeKey]) {
              spreadsheetErrors[errorCodeKey] = {};
              spreadsheetErrors[errorCodeKey].displayMsg = {};
              // This error isn't handled properly by the BE so we have to do weird stuff
              if (errorCodeKey === 108) {
                spreadsheetErrors[errorCodeKey].lotNumbers = [];
                spreadsheetErrors[errorCodeKey].invNumbers = [];
              } else {
                // Use sets here to avoid duplicate lot numbers
                spreadsheetErrors[errorCodeKey].lotNumbers = new Set();
                spreadsheetErrors[errorCodeKey].invNumbers = new Set();
              }
              spreadsheetErrors[errorCodeKey].referenceMsg = new Set();
            }

            spreadsheetErrors[errorCodeKey].displayMsg = displayMsg;

            if (errorCodeKey === 108) {
              if (lotNumber && lotNumber !== null && lotNumber !== '' && lotNumber.length > 0) {
                spreadsheetErrors[errorCodeKey].lotNumbers.push(lotNumber);
              }
              if (invNumber && invNumber !== null && invNumber !== '' && invNumber.length > 0) {
                spreadsheetErrors[errorCodeKey].invNumbers.push(invNumber);
              }
            } else {
              if (lotNumber && lotNumber !== null && lotNumber !== '' && lotNumber.length > 0) {
                spreadsheetErrors[errorCodeKey].lotNumbers.add(lotNumber);
              }
              if (invNumber && invNumber !== null && invNumber !== '' && invNumber.length > 0) {
                spreadsheetErrors[errorCodeKey].invNumbers.add(invNumber);
              }
            }
            spreadsheetErrors[errorCodeKey].referenceMsg.add(referenceMsg);
          });

          const breakingSpreadsheetErrors = {};
          // For error 108 - pretend its a real BE/upload error and not just a spreadsheet error
          if (Object.keys(spreadsheetErrors).indexOf('108') >= 0) {
            breakingSpreadsheetErrors[108] = spreadsheetErrors[108];
          }

          // For error 166 - pretend its a real BE/upload error and not just a spreadsheet error
          if (Object.keys(spreadsheetErrors).indexOf('166') >= 0) {
            breakingSpreadsheetErrors[166] = spreadsheetErrors[166];
          }

          if (Object.keys(breakingSpreadsheetErrors).length > 0) {
            this.setBreakingError(breakingSpreadsheetErrors);
            return;
          }

          // Probably should be fixed on the BE not the FE
          if (type === 'live') {
            // Delete any reserve related errors... timed only permutation
            delete spreadsheetErrors['142-reserve'];
          }

          const lots = get(response, 'data.lots', []);
          if (lots.length > 0) {
            // Spreadsheet errors that the user needs to fix in Step 3B
            // Cause the Save & Exit link to disappear
            this.props.setUploadErrors(true);

            /**
             * Parse the spreadsheet errors to find all lots with errors for the filter.
             *
             * This has to be done here or the rows will be off when trying to delete items
             */
            let lotNumberErrorIndexSet = new Set();
            Object.keys(spreadsheetErrors).forEach(errorCode => {
              const error = spreadsheetErrors[errorCode];
              lotNumberErrorIndexSet = new Set([...lotNumberErrorIndexSet, ...error.lotNumbers]);
            });

            let lotInvErrorIndexSet = new Set();
            Object.keys(spreadsheetErrors).forEach(errorCode => {
              const error = spreadsheetErrors[errorCode];
              lotInvErrorIndexSet = new Set([...lotInvErrorIndexSet, ...error.invNumbers]);
            });

            /**
             * Add a unique ID attribute to each lot so that we know which lot to
             * delete since rowIndex is unreliable with filters
             */
            lots.forEach((lot, index) => {
              const { lotExt, lotNumber, invNumber } = lot;
              // FE workaround since we can't send a reserve field to the BE if its a live sale
              if (type === 'live') {
                delete lot.reserve;
              }

              lot.id = index;
              lot.hasError = lotNumberErrorIndexSet.has(lotNumber + lotExt) || lotInvErrorIndexSet.has(invNumber);
            });

            this.props.handleUpload(lots, spreadsheetErrors, warnings);
          } else {
            this.setBreakingError(spreadsheetErrors);
          }
        }
      };

      xhr.send(formData);
    } catch (error) {
      console.error(error);
    } // end try/catch
  };

  onCancel() {
    this.setState({ files: [] });
  }

  render() {
    const { acceptedFileList, multiple } = this.props;
    const {
      files,
      isUploading,
      postUploadErrors,
      postUploadErrorCount,
      preUploadError,
      preUploadErrorTitle,
      preUploadErrorText,
      progressPct,
      uploadFailed
    } = this.state;

    return (
      <Fragment>
        {preUploadError && (
          <InlineAlert
            titleText={preUploadErrorTitle}
            baseText={preUploadErrorText}
            showPhones={false}
          />
        )}
        {some(postUploadErrors)
          && Object.keys(postUploadErrors).map(keyIndex => {
            const error = postUploadErrors[keyIndex];
            // Do some logic here to make the error more friendly
            const { title, description } = getSpreadsheetError(
              keyIndex,
              error.displayMsg,
              error.lotNumbers,
            );

            let secondaryText = '';
            /**
             * For case 108 and 166 - don't show secondaryText bc error
             * message is not user friendly and not formatted like the others
             */
            if (keyIndex !== '108' && keyIndex !== '166') {
              // This could be an arr ay of columns to display to the user for invalid column errors
              secondaryText = [...error.referenceMsg].join(', ');
            }
            // TODO should't cut off text
            return (
              <InlineAlert
                key={keyIndex}
                titleText={title}
                baseText={description}
                secondaryText={secondaryText || ' '}
                showPhones={false}
              />
            );
          })}

        <div className="mt-3">
          <Dropzone
            accept={acceptedFileList}
            multiple={multiple}
            onDrop={droppedFiles => this.onDrop(droppedFiles)}
            onFileDialogCancel={() => this.onCancel()}
          >
            {({ getRootProps, getInputProps }) => {
              if (!isUploading) {
                return (
                  <div className="dropzone-container cursor-pointer" {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="dropzone-container-text">
                      <h2>Drag Files Here</h2>
                      <FontAwesomeIcon color="gray" icon="plus" />
                      <p className="color-blue">or choose file(s) from your computer</p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="dropzone-container">
                  <div className="px-2 mb-3">
                    <FontAwesomeIcon className="fa-excel-icon" icon="file-excel" size="3x" />
                  </div>
                  <div>
                    <p className="mb-0">
                      {files[0].name.length > 50
                        ? `${files[0].name.substring(0, 50)}...`
                        : files[0].name}
                      {uploadFailed && (
                        <Button
                          color="link"
                          className="ml-2"
                          onClick={() => this.clearUploadErrors()}
                        >
                          Retry
                        </Button>
                      )}
                    </p>
                    <Progress color={uploadFailed ? 'danger' : ''} value={progressPct} />
                    {uploadFailed ? (
                      <p className="color-danger">
                        <FontAwesomeIcon
                          className="mr-1"
                          icon="exclamation-triangle"
                          color="danger"
                        />
                        Upload Failed: {postUploadErrorCount} Total Error
                        {postUploadErrorCount > 1 ? 's' : ''}
                      </p>
                    ) : (
                      <p className="text-muted">Uploading ...</p>
                    )}
                  </div>
                </div>
              );
            }}
          </Dropzone>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = ({ house }) => ({
  house
});

const mapDispatchToProps = {
  dispatchUpdateHouse: updateHouse,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Step3Dropzone));
